import React, { useState } from 'react';
import { Upload, Bell, FileText, CheckCircle, Mail, Phone, Clock, Users, Zap, Target } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useResumeService } from '../../hooks/useResumeService';

type TabType = 'free' | 'premium' | 'resume';

export default function TabbedServiceBanner() {
  const [activeTab, setActiveTab] = useState<TabType>('free');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { submitRequest, loading: resumeLoading } = useResumeService();

  const handleFreeSignup = () => {
    if (user) {
      navigate('/profile');
    } else {
      navigate('/signup');
    }
  };

  const handleResumeContact = async (e: React.FormEvent) => {
    e.preventDefault();

    const success = await submitRequest({
      name,
      email,
      phone
    });

    if (success) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
      setEmail('');
      setName('');
      setPhone('');
    }
  };

  return (
    <div className="py-16 bg-gradient-to-br from-amber-50 to-orange-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
          <p className="text-gray-600">Find the perfect option for your ranch job search</p>
        </div>

        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('free')}
              className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                activeTab === 'free'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="text-lg">Free</div>
              <div className="text-sm opacity-90">Get Started</div>
            </button>
            <button
              onClick={() => setActiveTab('premium')}
              className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                activeTab === 'premium'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="text-lg">Premium</div>
              <div className="text-sm opacity-90">$25/month</div>
            </button>
            <button
              onClick={() => setActiveTab('resume')}
              className={`flex-1 py-4 px-6 text-center font-semibold transition-colors ${
                activeTab === 'resume'
                  ? 'bg-amber-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="text-lg">Resume Service</div>
              <div className="text-sm opacity-90">$50 one-time</div>
            </button>
          </div>

          <div className="p-8 md:p-12">
            {activeTab === 'free' && (
              <div className="space-y-8 animate-fade-in">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Start Free - Apply with Saved Profile
                  </h3>
                  <p className="text-gray-600">
                    Create your account and upload your resume once - apply to unlimited jobs with one click
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <Upload className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Upload Once, Apply Anywhere</h4>
                      <p className="text-sm text-gray-600">Save your resume and cover letter to your profile</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">One-Click Applications</h4>
                      <p className="text-sm text-gray-600">Apply to jobs instantly with your saved profile</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Profile Management</h4>
                      <p className="text-sm text-gray-600">Update your information anytime</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Browse All Jobs</h4>
                      <p className="text-sm text-gray-600">Search ranch and AG jobs nationwide</p>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    onClick={handleFreeSignup}
                    className="px-8 py-4 bg-amber-600 text-white text-lg font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    {user ? 'Go to Profile' : 'Create Free Account'}
                  </button>
                  <p className="mt-3 text-sm text-gray-500">No credit card required</p>
                </div>
              </div>
            )}

            {activeTab === 'premium' && (
              <div className="space-y-8 animate-fade-in">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Premium - Never Miss an Opportunity
                  </h3>
                  <p className="text-gray-600">
                    Automatic job applications and instant alerts save you time and help you land jobs faster
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Auto-Submit to Matching Jobs</h4>
                      <p className="text-sm text-gray-600">Set your criteria and we apply for you automatically</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <Bell className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Instant Job Alerts</h4>
                      <p className="text-sm text-gray-600">Email and SMS notifications for new jobs</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Priority Application</h4>
                      <p className="text-sm text-gray-600">Your application goes to the top of the list</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Time-Saving Automation</h4>
                      <p className="text-sm text-gray-600">Apply to 10+ jobs while you work</p>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <a
                    href="https://buy.stripe.com/eVq9AS42ZbdEeYFg8m0co03"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-4 bg-amber-600 text-white text-lg font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    Subscribe Now - $25/month
                  </a>
                  <p className="mt-3 text-sm text-gray-500">Cancel anytime - No commitment</p>
                  {!user && (
                    <p className="mt-2 text-sm text-amber-600 font-medium">Free account required to subscribe</p>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'resume' && (
              <div className="space-y-8 animate-fade-in">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Professional AG Resume - $50
                  </h3>
                  <p className="text-gray-600">
                    Specialized resumes for ranch hands and agricultural professionals
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Personal Phone Interview</h4>
                      <p className="text-sm text-gray-600">We talk through your ranch and AG experience</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">24-Hour Turnaround</h4>
                      <p className="text-sm text-gray-600">Get your polished resume fast</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Unlimited Revisions</h4>
                      <p className="text-sm text-gray-600">Changes included until you're satisfied</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-lg">
                    <div className="p-3 bg-amber-600 rounded-lg flex-shrink-0">
                      <Users className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">AG Industry Experts</h4>
                      <p className="text-sm text-gray-600">We know what ranches and farms look for</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-6">
                  <p className="text-center text-gray-700 mb-4">
                    <span className="font-semibold">We specialize in resumes for ranch hands and AG professionals.</span>
                    <br />
                    Your unique farm and ranch experience deserves professional presentation.
                  </p>
                </div>

                <div className="text-center">
                  <a
                    href="https://buy.stripe.com/eVq14m1UR0z0aIp3lA0co00"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-8 py-4 bg-amber-600 text-white text-lg font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                  >
                    Get Professional Resume - $50
                  </a>
                  <p className="mt-3 text-sm text-gray-500">One-time payment - Get started today</p>
                </div>

                {showSuccess && (
                  <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
                    <div className="flex items-center gap-3 px-6 py-4 rounded-lg shadow-lg border bg-green-50 border-green-200">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                      <p className="text-sm font-medium text-green-800">
                        Thank you! We'll contact you within 24 hours to schedule your consultation.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
